import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Wind, Moon, Flame, Waves, HeartHandshake, Sparkle, Palette } from 'lucide-react'
import { cn } from '@/lib/utils'
import { BlurFade } from '@/components/ui/blur-fade'

const NEEDS = [
  {
    slug: 'stress-burnout',
    Icon: Wind,
    title: 'Stress & Burnout',
    back: 'Find breath, movement, and circle-based practices that help your nervous system come down. We’ll help you start where you are.',
  },
  {
    slug: 'rest-recovery',
    Icon: Moon,
    title: 'Rest & Recovery',
    back: 'Yin, restorative yoga, sound baths, and weekend retreats designed to actually slow you down. Pick one this week.',
  },
  {
    slug: 'movement-energy',
    Icon: Flame,
    title: 'Movement & Energy',
    back: 'Vinyasa, dance, strength, and embodied play — classes that match your body’s mood, not just a schedule.',
  },
  {
    slug: 'emotional-balance',
    Icon: Waves,
    title: 'Emotional Balance',
    back: 'Tools for regulation, processing, and feeling steadier — guided by practitioners who hold space well.',
  },
  {
    slug: 'community-connection',
    Icon: HeartHandshake,
    title: 'Community & Connection',
    back: 'Sunday circles, weekly check-ins, and small groups you can return to. You don’t have to do this alone.',
  },
  {
    slug: 'spiritual-exploration',
    Icon: Sparkle,
    title: 'Spiritual Exploration',
    back: 'Meditation, ritual, and inner inquiry — gentle entry points and longer journeys for when you’re ready.',
  },
  {
    slug: 'creativity-expression',
    Icon: Palette,
    title: 'Creativity & Expression',
    back: 'Sound, movement, art, and play — practices that loosen what’s stuck and let something new move through.',
  },
]

function NeedCard({ need, isOpen, onToggle }) {
  const { Icon, title, back, slug } = need
  return (
    <div className="h-60 [perspective:1200px]">
      <div
        className={cn(
          'relative h-full w-full transition-transform duration-[400ms] ease-out [transform-style:preserve-3d]',
          isOpen && '[transform:rotateY(180deg)]'
        )}
      >
        {/* Front */}
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-label={`${title} — learn more`}
          className="absolute inset-0 flex flex-col items-center justify-center gap-4 rounded-2xl border border-primary-100 bg-white p-6 text-center shadow-sm transition-shadow hover:-translate-y-0.5 hover:shadow-md [backface-visibility:hidden] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          <Icon className="h-8 w-8 text-primary-600" strokeWidth={1.5} />
          <span className="text-lg font-semibold text-gray-900">{title}</span>
        </button>

        {/* Back */}
        <div className="absolute inset-0 flex flex-col rounded-2xl border border-primary-200 bg-primary-50 p-6 text-left [backface-visibility:hidden] [transform:rotateY(180deg)]">
          <button
            type="button"
            onClick={onToggle}
            aria-label={`Close ${title}`}
            className="flex-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <span className="text-base font-semibold text-primary-800">
              {title}
            </span>
            <p className="mt-2 text-sm leading-relaxed text-gray-700">{back}</p>
          </button>
          <Link
            to={`/events?need=${slug}`}
            className="mt-3 inline-flex items-center gap-1 self-start text-sm font-semibold text-primary-700 hover:text-primary-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            See what&apos;s on →
          </Link>
        </div>
      </div>
    </div>
  )
}

export function ExploreByNeedSection() {
  const [openSlug, setOpenSlug] = useState(null)

  return (
    <section className="bg-cream">
      <div className="mx-auto max-w-[1200px] px-6 py-16 lg:px-20 lg:py-20">
        <BlurFade>
          <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
            Where are you right now?
          </h2>
          <p className="mt-3 max-w-2xl text-lg text-gray-700">
            Choose a starting point — we&apos;ll help you find practices that meet
            you there.
          </p>
        </BlurFade>

        <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {NEEDS.map((need) => (
            <NeedCard
              key={need.slug}
              need={need}
              isOpen={openSlug === need.slug}
              onToggle={() =>
                setOpenSlug((prev) => (prev === need.slug ? null : need.slug))
              }
            />
          ))}
        </div>
      </div>
    </section>
  )
}
